// Deep-link helpers.
//
// Push notifications open URLs like:
//   /app?screen=connect&tab=prayer&prayer_id=...
//   /app?screen=bible&book=Romans&chapter=8&verse=2&open=comments
//   /app?screen=notifications&filter=access_requests
//   /app?screen=plan&plan_id=...&day=3
//
// We capture those params at cold start (before any auth redirect can drop
// them), stash them in sessionStorage so they survive a logged-out ->
// /login -> /app round-trip, and apply them once the app is loaded and auth
// is confirmed. After applying we clear both the stash and the URL params so
// a refresh doesn't re-trigger the navigation.

const KEYS = ['screen', 'tab', 'prayer_id', 'post_id', 'book', 'chapter', 'verse', 'open', 'filter', 'plan_id', 'day'];
const STORE_KEY = 'nlc.deepLink';

export function parseDeepLinkFromSearch(search) {
  try {
    const sp = new URLSearchParams(search || '');
    if (!sp.get('screen')) return null;
    const out = {};
    for (const k of KEYS) {
      const v = sp.get(k);
      if (v != null) out[k] = v;
    }
    return out.screen ? out : null;
  } catch {
    return null;
  }
}

// Run once at cold start (called from App.js module scope). Stashes any deep
// link present in the URL so it survives the auth/login round-trip.
export function captureDeepLink() {
  if (typeof window === 'undefined') return;
  const dl = parseDeepLinkFromSearch(window.location.search);
  if (dl) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(dl)); } catch (_e) { /* ignore */ }
  }
}

// Returns a pending deep link (prefer the live URL, fall back to the stash).
export function takeDeepLink() {
  if (typeof window === 'undefined') return null;
  let dl = parseDeepLinkFromSearch(window.location.search);
  if (!dl) {
    try {
      const s = sessionStorage.getItem(STORE_KEY);
      if (s) dl = JSON.parse(s);
    } catch (_e) { /* ignore */ }
  }
  return dl;
}

// Clear the stash and strip the deep-link params from the URL (so a refresh
// won't re-navigate). Leaves any unrelated query params untouched.
export function clearDeepLink() {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(STORE_KEY); } catch (_e) { /* ignore */ }
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const k of KEYS) {
      if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
    }
    if (changed) {
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  } catch (_e) { /* ignore */ }
}
